<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue';
import type { AttractExperimentVariant } from '@/shared/types/api';
import type { SplashVideoFailure } from './useHomeSplashscreen';

interface Props {
  backgroundUrl: string | null;
  isVideoEnabled: boolean;
  preloadVideos?: readonly AttractExperimentVariant[];
  showVideo: boolean | string | null | undefined;
  videoUrl: string | null | undefined;
}

withDefaults(defineProps<Props>(), {
  preloadVideos: () => [],
});

const emit = defineEmits<{
  dismiss: [];
  preloadFailed: [variant: string, failure: SplashVideoFailure];
  videoFailed: [failure: SplashVideoFailure];
  videoLoaded: [];
  videoStarted: [];
}>();

const splashVideo = useTemplateRef<HTMLVideoElement>('splashVideo');
const preloadVideoElements = useTemplateRef<HTMLVideoElement[]>('preloadVideo');

function readFailure(event: Event): SplashVideoFailure {
  const error = (event.target as HTMLVideoElement | null)?.error ?? null;
  return { code: error?.code ?? null, message: error?.message || null };
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onDismiss(): void {
  emit('dismiss');
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onVideoLoaded(): void {
  emit('videoLoaded');
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onVideoPlaying(): void {
  emit('videoStarted');
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onVideoError(event: Event): void {
  emit('videoFailed', readFailure(event));
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onPreloadError(variant: string, event: Event): void {
  emit('preloadFailed', variant, readFailure(event));
}

function releaseVideo(video: HTMLVideoElement | null | undefined): void {
  if (!video) {
    return;
  }
  video.pause();
  video.removeAttribute('src');
  video.load();
}

onBeforeUnmount(() => {
  releaseVideo(splashVideo.value);
  for (const video of preloadVideoElements.value ?? []) {
    releaseVideo(video);
  }
});
</script>

<template>
  <template v-if="backgroundUrl">
    <img
      class="Home__splashscreen-bg absolute-inset-0"
      data-testid="home-splashscreen-background"
      :src="backgroundUrl"
    />
    <div class="Home__splashscreen-overlay absolute-inset-0"/>
  </template>

  <div
    v-if="videoUrl && isVideoEnabled"
    v-show="showVideo"
    class="Home__splashscreen absolute-inset-0"
    data-testid="home-splashscreen"
    @click="onDismiss"
  >
    <video
      ref="splashVideo"
      autoplay
      class="Home__splashscreen-video absolute-inset-0"
      data-testid="home-splashscreen-video"
      loop
      muted
      playsinline
      :src="videoUrl"
      @canplay="onVideoLoaded"
      @error="onVideoError"
      @playing="onVideoPlaying"
    />
  </div>

  <div
    v-if="isVideoEnabled && preloadVideos.length > 0"
    aria-hidden="true"
    class="Home__splashscreen-preload"
  >
    <video
      v-for="video in preloadVideos"
      :key="video.key"
      ref="preloadVideo"
      data-testid="home-splashscreen-preload"
      muted
      playsinline
      preload="auto"
      :src="video.video_url ?? undefined"
      @error="onPreloadError(video.key, $event)"
    />
  </div>
</template>

<style scoped lang="scss">
.Home__splashscreen-bg {
  z-index: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.Home__splashscreen-overlay {
  z-index: 0;
  background: rgba(0, 0, 0, 0.4);
}

.Home__splashscreen {
  z-index: 10;
}

.Home__splashscreen-video {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.Home__splashscreen-preload {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
